import { useApolloClient } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import type { Payment, RequeryPaymentInput, SuggestedAllocation } from '@/types/composition-functions';
import { PaymentStatusEnum } from '@/types/composition-functions';
import type {
  GetPaymentReceiptPdfQuery,
  GetPaymentReceiptPdfQueryVariables,
  SuggestPaymentAllocationQuery,
  SuggestPaymentAllocationQueryVariables,
} from '@/types/operations';
import {
  useGetPaymentsLazyQuery,
  useRecordPaymentMutation,
  useVoidPaymentMutation,
  useRequeryPaymentMutation,
  GetPaymentReceiptPdfDocument,
  SuggestPaymentAllocationDocument,
} from '@/types/vue-apollo-helpers';
import { evictBillingQueries } from '@/utils/billingCache';
import { paymentStatusLabel } from '@/utils/billingLabels';
import { downloadPdf } from '@/utils/downloadPdf';
import { toUserMessage } from '@/utils/error';

// Re-export for consumers
export type { Payment, SuggestedAllocation };

/**
 * Status choices for the Transactions filter.
 *
 * The `payments` query types `status` as PaymentStatusEnum, so the value sent
 * over the wire must be the enum's GraphQL NAME ('PENDING') — not the database
 * value ('pending') that Lighthouse maps it to. Posting the lowercase form made
 * variable coercion fail, so the whole query errored and every status filter
 * looked like "no transactions" instead of a filtered list. Building the list
 * from the generated enum is what keeps the two from drifting apart again.
 */
export const PAYMENT_STATUS_FILTER_OPTIONS: Array<{ label: string; value: PaymentStatusEnum }> = [
  PaymentStatusEnum.Pending,
  PaymentStatusEnum.Successful,
  PaymentStatusEnum.Failed,
  PaymentStatusEnum.Refunded,
  PaymentStatusEnum.PartiallyRefunded,
  PaymentStatusEnum.Voided,
].map((value) => ({ label: paymentStatusLabel(value), value }));

export const usePayments = (
  variables?: Ref<{ student_id?: string; status?: PaymentStatusEnum; session_id?: string; academic_calendar_id?: string }>,
  options: { autoFetch?: boolean } = {},
) => {
  // cache-and-network: gateway webhooks and other staff record payments too,
  // so a revisit shows the cached list at once and then the fresh one.
  const {
    result,
    loading,
    error,
    refetch,
    load,
  } = useGetPaymentsLazyQuery(variables, { fetchPolicy: 'cache-and-network' });
  if (options.autoFetch) load();

  const payments = computed(() => result.value?.payments ?? []);

  // `payments` is non-null in the schema, so any error nulls the whole list —
  // without `error` a failed request is indistinguishable from "no payments".
  return { payments, loading, error, refetch, load };
};

export const useRecordPayment = () => {
  const {
    mutate: recordPaymentMutation,
    loading: mutationLoading,
    onDone,
    onError,
  } = useRecordPaymentMutation({
    update: evictBillingQueries,
  });

  // Apollo's `loading` only flips on the tick AFTER the call, and the batch link
  // merges everything fired inside its window into one request — so two fast
  // clicks both reach the server. Production hit exactly that: the second insert
  // died on the unique transaction-reference index while the first had already
  // taken the money. This synchronous flag closes the window before it opens.
  const inFlight = ref(false);

  const recordPayment = async(input: {
    student_id: string;
    amount: number;
    payment_method: string;
    payment_date: string;
    transaction_reference?: string;
    notes?: string;
    credit_overpayment?: boolean;
    allocations: Array<{ bill_detail_id: string; amount: number }>;
  }) => {
    if (inFlight.value) return undefined;

    inFlight.value = true;
    try {
      return await recordPaymentMutation({ input });
    } finally {
      inFlight.value = false;
    }
  };

  return {
    recordPayment,
    /** True from the moment the click lands, not from Apollo's next tick. */
    loading: computed(() => inFlight.value || mutationLoading.value),
    onDone,
    onError,
  };
};

export const useVoidPayment = () => {
  const {
    mutate: voidPayment,
    loading,
    onDone,
    onError,
  } = useVoidPaymentMutation({
    update: evictBillingQueries,
  });

  return {
    voidPayment: (id: string, reason: string) => voidPayment({ id, reason }),
    loading,
    onDone,
    onError,
  };
};

export const useRequeryPayment = () => {
  const {
    mutate: requeryPayment,
    loading,
    onDone,
    onError,
  } = useRequeryPaymentMutation({
    update: evictBillingQueries,
  });

  return {
    requeryPayment: (input: RequeryPaymentInput) => requeryPayment({ input }),
    loading,
    onDone,
    onError,
  };
};

export const SUGGEST_FAILED_MESSAGE = 'We couldn\'t split the payment automatically. Pick the fees yourself below.';

/**
 * "Split across unpaid fees": the server's suggested split for an amount.
 *
 * Always asks the network. The lazy-query version deduplicated a repeat
 * request with the same student and amount (so the button spun forever), and
 * a cached answer could split money onto fees that had just been paid.
 */
export const useSuggestAllocation = () => {
  const { client } = useApolloClient();
  const suggestions = ref<SuggestedAllocation[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const suggest = async(studentId: string, amount: number): Promise<SuggestedAllocation[]> => {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await client.query<SuggestPaymentAllocationQuery, SuggestPaymentAllocationQueryVariables>({
        query: SuggestPaymentAllocationDocument,
        variables: { student_id: studentId, amount },
        fetchPolicy: 'network-only',
      });
      suggestions.value = (data?.suggestPaymentAllocation ?? []) as SuggestedAllocation[];
    } catch (e) {
      suggestions.value = [];
      error.value = toUserMessage(e, SUGGEST_FAILED_MESSAGE);
    } finally {
      loading.value = false;
    }
    return suggestions.value;
  };

  return { suggestions, loading, error, suggest };
};

export const RECEIPT_FAILED_MESSAGE = 'We couldn\'t create the receipt. Try again in a moment.';

/**
 * Download a payment's receipt PDF. One network request per click, the row
 * spinner always stops, and a failure says so instead of spinning forever.
 */
export const useReceiptDownload = () => {
  const { client } = useApolloClient();
  const toast = useToast();
  const downloadingId = ref<string | null>(null);

  const download = async(paymentId: string): Promise<boolean> => {
    if (downloadingId.value) return false;
    downloadingId.value = paymentId;
    try {
      const { data } = await client.query<GetPaymentReceiptPdfQuery, GetPaymentReceiptPdfQueryVariables>({
        query: GetPaymentReceiptPdfDocument,
        variables: { payment_id: paymentId },
        fetchPolicy: 'network-only',
      });
      const pdf = data?.paymentReceiptPdf;
      if (!pdf?.base64) throw new Error(RECEIPT_FAILED_MESSAGE);
      downloadPdf(pdf.base64, pdf.filename);
      return true;
    } catch (e) {
      toast.add({
        severity: 'error',
        summary: 'Couldn\'t download the receipt',
        detail: toUserMessage(e, RECEIPT_FAILED_MESSAGE),
        life: 6000,
      });
      return false;
    } finally {
      downloadingId.value = null;
    }
  };

  return { downloadingId, download };
};
